import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, type CaseListItem } from '../lib/api'
import type { CaseStatus } from '../../shared/types'
import { SeverityBadge, StatusBadge } from '../components/Badges'
import { formatDate } from '../lib/format'

export function SearchPage() {
  const [params] = useSearchParams()
  // Arrive pre-filled from the dashboard quick search or a stat tile.
  const incoming = params.get('q') ?? ''
  const incomingFrom = params.get('from') ?? ''
  const incomingTo = params.get('to') ?? ''
  const incomingStatus = (params.get('status') ?? '') as CaseStatus | ''

  const [q, setQ] = useState(incoming)
  const [status, setStatus] = useState<CaseStatus | ''>(incomingStatus)
  const [from, setFrom] = useState(incomingFrom)
  const [to, setTo] = useState(incomingTo)
  const [results, setResults] = useState<CaseListItem[] | null>(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  async function doSearch(term: string, st: CaseStatus | '', f: string, t: string) {
    setLoading(true)
    try {
      const res = await api.searchCases({ q: term, status: st, from: f, to: t })
      setResults(res.cases)
    } finally {
      setLoading(false)
    }
  }

  // Any incoming params (query and/or a date window) run the search on arrival.
  useEffect(() => {
    if (incoming || incomingFrom || incomingTo || incomingStatus) {
      setQ(incoming)
      setStatus(incomingStatus)
      setFrom(incomingFrom)
      setTo(incomingTo)
      void doSearch(incoming, incomingStatus, incomingFrom, incomingTo)
    }
  }, [incoming, incomingFrom, incomingTo, incomingStatus])

  function onSearch(e: FormEvent) {
    e.preventDefault()
    void doSearch(q, status, from, to)
  }

  function clearFilters() {
    setQ('')
    setStatus('')
    setFrom('')
    setTo('')
    setResults(null)
  }

  return (
    <div>
      <div className="page-title">Search Cases</div>
      <p className="page-sub">Find cases by number, keyword, status, or date range.</p>

      <form className="card" onSubmit={onSearch}>
        <div className="field" style={{ marginBottom: 12 }}>
          <label>Case number or keyword</label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="CAS-000123, customer, address, technician…"
          />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as CaseStatus | '')}
              style={controlStyle}
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Date from</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={controlStyle} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Date to</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={controlStyle} />
          </div>
          <button className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </button>
          <button
            type="button"
            className="btn"
            style={{ border: '1px solid var(--line)' }}
            onClick={clearFilters}
          >
            Clear
          </button>
        </div>
      </form>

      {results && (
        <div className="card" style={{ padding: 0, marginTop: 16 }}>
          {results.length === 0 ? (
            <div style={{ padding: 20, color: 'var(--gray)' }}>No matching cases.</div>
          ) : (
            <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Case #</th>
                  <th>Address</th>
                  <th>Incident date</th>
                  <th>Status</th>
                  <th>Severity</th>
                </tr>
              </thead>
              <tbody>
                {results.map((k) => (
                  <tr key={k.id} className="row-link" onClick={() => navigate(`/app/cases/${k.id}`)}>
                    <td style={{ fontWeight: 600 }}>{k.case_number}</td>
                    <td>{k.address ?? '—'}</td>
                    <td>{formatDate(k.incident_date)}</td>
                    <td>
                      <StatusBadge status={k.status} />
                    </td>
                    <td>
                      <SeverityBadge severity={k.severity} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const controlStyle: React.CSSProperties = {
  padding: '9px 12px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-sm)',
  font: 'inherit',
  background: 'var(--bg)',
}
